import { useEffect, useState } from "react";
import Input from "../../../form/Input";
import Select from "../../../form/select/Select";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
import Textarea from "../../../form/Textarea";
import { useMutation, useQuery } from "@tanstack/react-query";
import currencyCodes from "currency-codes";
import getSymbolFromCurrency from "currency-symbol-map";
import Switch from "../../../form/Switch";
import DatePicker from "../../../form/DatePicker";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useDebounce } from "../../../../hooks/useDebounce";
import { planList } from "../../../../services/planService";
import { removeDuplicateFromArray } from "../../../../helpers";
import MultiselectDropdown from "../../../form/MultiselectDropdown";
import { discoutAddEdit } from "../../../../services/discountService";

const validation = {
  code: { required: true, message: "Discound Code is required" },
  amount: { required: true, message: "Amount is required" },
  usageLimit: { required: true, message: "Usage Limit is required" },
  startedAt: { required: true, message: "Start date is required" },
  expiresAt: { required: true, message: "End Limit is required" },
};

const ManageDiscount = ({ getResponse = () => {}, close = () => {}, data }) => {
  const [planFilters, setPlanFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });
  const [plankeyword, setPlankeyword] = useState("");
  const planSearch = useDebounce(plankeyword, 300);

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setErrors,
    setFieldsValue,
    formValidate,
  } = useForm({
    initialState: { discountType: "fixed" },
    validationRules: validation,
  });

  useEffect(() => {
    if (values.discountType === "fixed" && !values.currency) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        currency: {
          message: "Currency is required.",
          type: "required",
        },
      }));
    } else {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.currency;
        return newErrors;
      });
    }
  }, [values.discountType, values.currency]);

  useEffect(() => {
    if (values.discountType === "fixed") {
      setFieldsValue({
        currency: "USD",
      });
    }
  }, [values.discountType]);

  const currencyList = currencyCodes.data.map((curr) => ({
    name: `${curr.code} - ${curr.currency}`,
    value: curr.code,
  }));

  useEffect(() => {
    if (data?._id) {
      setFieldsValue({
        ...data,
      });
    }
  }, [data]);

  const {
    mutate,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationFn: async (val) => {
      const payload = {
        ...val,
      };
      val?.discountType === "percentage" && delete payload.currency;
      return await discoutAddEdit({
        ...payload,
        ...(data?._id && { id: data?._id }),
      });
    },
    onSuccess: (res) => {
      getResponse(res); // handle success response
      return res;
    },
    onError: (err) => {
      console.error("Tax Rate Submission Error:", err);
    },
  });

  const { data: plans } = useQuery({
    queryKey: ["plan", planFilters, planSearch],
    queryFn: async () => {
      const res = await planList({
        ...planFilters,
        ...(planSearch && { keyword: planSearch }),
      });
      if (res?.docs?.status === 200) {
        return {
          ...res?.docs,
          data: removeDuplicateFromArray([
            ...(plans?.data || []),
            ...res?.docs?.data,
          ]),
        };
      } else {
        if (plans?.data?.length > 0) {
          return plans;
        } else {
          return res;
        }
      }
    },
  });

  function loadData(e, obj, setData = () => {}) {
    if (
      Math.ceil(e.target.scrollTop) + 20 >=
        e.target.scrollHeight - e.target.clientHeight &&
      obj?.metadata?.hasNextPage
    ) {
      setData((pre) => ({
        ...pre,
        page: obj?.metadata?.nextPage,
      }));
    }
  }

  return (
    <form onSubmit={(event) => handleSubmit(event, mutate)}>
      <div className="grid grid-cols-12 gap-4 p-4">
        {/* Name */}
        <div className="col-span-6">
          <Input
            label="Coupon Code"
            inputPlaceholder="Enter Coupon Code"
            inputName="code"
            value={values?.code || ""}
            onChange={handleChange}
            {...formValidate(errors, "code")}
          />
        </div>

        {/* Type */}
        <div className="col-span-6">
          <Select
            label="Discount Type"
            placeholder={"Select Discount Type"}
            selectedValue={values?.discountType || ""}
            dropdownData={[
              { name: "Percentage", value: "percentage" },
              { name: "Fixed Amount", value: "fixed" },
            ]}
            onChange={(val) =>
              handleChange({ target: { name: "discountType", value: val } })
            }
            {...formValidate(errors, "discountType")}
          />
        </div>

        {/* Conditional fields based on type */}
        {values?.discountType === "percentage" ? (
          <div className="col-span-6">
            <Input
              label="Percentage discount"
              inputPlaceholder="Enter Percentage"
              inputName="amount"
              inputType="number"
              inputSuffix="%"
              value={values?.amount || ""}
              onChange={handleChange}
              {...formValidate(errors, "amount")}
            />
          </div>
        ) : (
          <>
            <div className="flex gap-2 col-span-6">
              <Input
                label="Fixed discount amount"
                inputPlaceholder="Enter amount"
                inputName="amount"
                inputClasses="!px-12"
                inputType="number"
                value={values?.amount || ""}
                inputPrefix={getSymbolFromCurrency(values?.currency)}
                onChange={handleChange}
                {...formValidate(errors, "amount")}
              />
              <div className="relative w-20">
                <Select
                  isSearch
                  required
                  label="Currency"
                  placeholder="Select Currency"
                  selectedValue={values?.currency}
                  dropdownData={currencyList}
                  onChange={(val) =>
                    handleChange({ target: { name: "currency", value: val } })
                  }
                  {...formValidate(errors, "currency")}
                />
              </div>
            </div>
          </>
        )}

        {/* New Number Input */}
        <div className="col-span-6">
          <Input
            label="Usage Limit"
            inputPlaceholder="Enter limit"
            inputType="number"
            inputName="usageLimit"
            value={values?.usageLimit || ""}
            onChange={handleChange}
            {...formValidate(errors, "usageLimit")}
          />
        </div>

        {/* Apply to Specific Products */}
        <div className="col-span-12 space-y-2">
          <label
            className="text-sm font-medium text-gray-500"
            htmlFor="applySpecific"
          >
            Apply to specific plans
          </label>
          <Switch
            name="isSpecificProduct"
            className="!w-10 !h-5 before:!w-4 before:!h-4 peer-checked:before:!left-[22px]"
            checked={values?.isSpecificProduct || false}
            onChange={() =>
              handleChange({
                target: {
                  name: "isSpecificProduct",
                  value: !values?.isSpecificProduct,
                },
              })
            }
            {...formValidate(errors, "isSpecificProduct")}
          />
          {values?.isSpecificProduct && (
            // <Select
            //   label="Apply to Specific Products"
            //   placeholder="Choose Products"
            //   selectedValue={values?.products}
            //   isMulti
            //   dropdownData={productList.map((prod) => ({
            //     name: `${prod?.name} - ${prod?.price}`,
            //     value: prod?.id,
            //   }))}
            //   onChange={(val) => {
            //     console.log({ val });

            //     handleChange({ target: { name: "products", value: val } });
            //   }}
            // />
            <MultiselectDropdown
              label={"Select question"}
              dropdownData={removeDuplicateFromArray(
                [
                  //   ...(data?.questionDetails || [])?.map((item) => ({
                  //     name: item?.title,
                  //     value: item?._id,
                  //   })),
                  ...(plans?.data || [])?.map((q) => ({
                    name: q?.title,
                    value: q?._id,
                  })),
                ],
                "value"
              )}
              values={values?.appliesToPlans}
              onChange={(val) => {
                handleChange({
                  target: { name: "appliesToPlans", value: val },
                });
              }}
              onScroll={(e) => {
                loadData(e, plans, setPlanFilters);
              }}
            />
          )}
        </div>

        {/* Start Date */}
        <div className="col-span-6">
          <DatePicker
            label={"Start Date"}
            placeholder="Start Date"
            value={{
              startDate: values?.startedAt,
              endDate: values?.startedAt,
            }}
            onChange={(e) => {
              const end = new Date(values?.expiresAt);

              const start = new Date(e?.startDate);
              if (values?.deadline) {
                if (start > end) {
                  handleChange({
                    target: {
                      value: null,
                      name: "startedAt",
                    },
                  });
                  toast.info("Open date must be smaller then close date");
                } else {
                  handleChange({
                    target: {
                      value: dayjs(e?.startDate).format(
                        "YYYY-MM-DDT00:00:00.000[Z]"
                      ),
                      name: "startedAt",
                    },
                  });
                }
              } else {
                handleChange({
                  target: {
                    value: dayjs(e?.startDate).format(
                      "YYYY-MM-DDT00:00:00.000[Z]"
                    ),
                    name: "startedAt",
                  },
                });
              }
            }}
            {...formValidate(errors, "startedAt")}
          />
        </div>

        {/* End Date */}
        <div className="col-span-6">
          <DatePicker
            label={"End Date"}
            placeholder="End Date"
            value={{
              startDate: values?.expiresAt,
              endDate: values?.expiresAt,
            }}
            onChange={(e) => {
              const start = new Date(values?.startedAt);
              const end = new Date(e?.startDate);

              if (values?.startedAt) {
                if (start > end) {
                  handleChange({
                    target: {
                      value: null,
                      name: "expiresAt",
                    },
                  });
                  toast.info("Close date must be smaller then open date");
                } else {
                  handleChange({
                    target: {
                      value: dayjs(e?.startDate).format(
                        "YYYY-MM-DDT00:00:00.000[Z]"
                      ),
                      name: "expiresAt",
                    },
                  });
                }
              }
            }}
            {...formValidate(errors, "expiresAt")}
            isDisabled={values.onGoing}
          />
        </div>

        {/* Description */}
        <div className="col-span-12">
          <Textarea
            label="Description"
            inputPlaceholder="Add coupon description"
            inputClasses="!h-40"
            inputName="description"
            inputValue={values?.description || ""}
            onChange={handleChange}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="relative col-span-4 mt-5">
        <div className="flex items-center justify-center gap-4">
          <Button
            buttonType="submit"
            buttonLabel={"Create Coupon"}
            buttonClasses={
              "!rounded-full !px-8 !h-12 !border !border-primary !bg-transparent !text-primary hover:!bg-primary hover:!text-white"
            }
            buttonLabelClasses="mx-auto"
            {...(isLoading && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
          />
          <Button
            buttonLabel="Cancel"
            buttonClasses={
              "!rounded-full !px-8 !h-12 !border !border-primary !bg-transparent !text-primary hover:!bg-primary hover:!text-white"
            }
            buttonLabelClasses="mx-auto"
            buttonFunction={close}
          />
        </div>
      </div>
    </form>
  );
};

export default ManageDiscount;
